import { OrderType } from 'apiConnector/secondary-market/batch/model';
import { useNavigateTo } from 'hooks/useNavigateTo';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import { NextWorkflowError } from 'models/NextWorkflowError';
import { useMemo } from 'react';
import { useMapMessage } from 'routes/secondary-market-batch/grid/hooks/use-map-message';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { openInModifyCpOrderScreenThunk } from 'state/secondaryMarket/secondaryMarket.thunks';
import { RowData } from 'state/workspaces/secondary-market/batch/model';
import { ApiMessage, BackendApiMessage } from 'utils/http/httpClient.model';

const toWorkflowError = (message: ApiMessage): NextWorkflowError => ({
  originMessage: message.message ?? '',
  rejectionOrigin: (message as BackendApiMessage).origin,
});

export function useMessages(orderType: OrderType, data?: RowData) {
  const mapMessage = useMapMessage(orderType);
  return useMemo(() => {
    const messages = data?.metadata?.messages ?? [];
    const columnMessages = Object.values(data?.metadata?.messageByColumns ?? {}).flat();
    const allMessages = [...messages, ...columnMessages].map(m => ({ ...m, message: mapMessage(m) }));

    const errors = allMessages.filter(m => m.level === 'Error').map(toWorkflowError);
    const criticalErrors = allMessages.filter(m => m.level === 'Critical').map(toWorkflowError);
    const hasCriticalErrors = criticalErrors.length > 0;
    const hasBackendMessage = allMessages.some(m => (m as BackendApiMessage).origin !== undefined);
    return {
      hasMessages: allMessages.filter(m => m.level !== 'Info').length > 0,
      warnings: allMessages.filter(m => m.level === 'Warning' || m.level === 'Error'),
      allErrors: [...criticalErrors],
      errors,
      hasCriticalErrors,
      hasBackendMessage,
    };
  }, [data?.metadata?.messages, data?.metadata?.messageByColumns, mapMessage]);
}

export function useGetSuccessAction(orderType: OrderType, data?: RowData) {
  const dispatch = useThunkDispatch();
  const navigateToTradeBlotter = useNavigateTo('/secondary-trade-blotter');
  const navigateToPostEventWorkspace = useNavigateTo('/secondary-market-post-event-workspace');

  let isSuccessActionEnabled = false;
  let onSuccessAction;
  let successActionLabel = '';

  if (orderType === 'cp-order') {
    isSuccessActionEnabled =
      data?.metadata?.availableActions.some(({ type, enabled }) => type === 'open-in-secondary-market' && enabled) ??
      false;
    onSuccessAction = async () => {
      if (!data?.orderId) {
        return;
      }

      await dispatch(openInModifyCpOrderScreenThunk([data.orderId]));
      navigateToPostEventWorkspace();
      logAnalytics.trackEvent('Secondary market batch', 'Open secondary post trade');
    };
    successActionLabel = data?.orderId ?? '';
  }

  if (orderType === 'ebiz-quote') {
    isSuccessActionEnabled = true;
    onSuccessAction = () => {
      navigateToTradeBlotter();
      logAnalytics.trackEvent('Secondary market batch', 'Open secondary trade blotter');
    };
    successActionLabel = data?.quoteId ?? '';
  }

  return { isSuccessActionEnabled, onSuccessAction, successActionLabel };
}
